/**
 * The Tier 1 and Tier 2 forms, ONCE.
 *
 * They are reached from two directions and they must behave identically from both:
 *
 *   during onboarding      the new participant walks the whole ladder in one sitting
 *   from a tier GATE       an existing participant is bounced here by an action they cannot yet take
 *
 * If these were written twice, they would drift, and the version that drifted would be the one a
 * user reached at the moment they were already frustrated by being refused. Worse, a field added to
 * one and not the other means a participant verified through the gate has a different, thinner
 * identity record than one verified through onboarding — and the KYC file is the thing a regulator
 * reads. There is one implementation.
 */

import { CountrySelect } from '@/components/country-select';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, LipaApiError } from '@/lib/api';

export type VerifyOutcome = 'verified' | 'under_review';

function useFail() {
  const [error, setError] = useState<string | null>(null);

  const fail = (e: unknown) =>
    setError(
      e instanceof LipaApiError ? e.detail.message : 'Something went wrong. Please try again.',
    );

  return { error, setError, fail };
}

/**
 * TIER 1. Identity document, and a selfie matched to it.
 *
 * The name field is the one that matters, and its label says so. It is the string that gets screened
 * against sanctions and PEP lists: a nickname here means the platform screened a person who does not
 * exist and recorded a clean result about them.
 */
export function Tier1Form({
  participantId,
  onDone,
}: {
  participantId: string;
  onDone: (outcome: VerifyOutcome) => void;
}) {
  const { error, setError, fail } = useFail();
  const [busy, setBusy] = useState(false);

  const [fullName, setFullName] = useState('');
  const [dob, setDob] = useState('');
  const [nationality, setNationality] = useState('KE');
  const [docType, setDocType] = useState<'national_id' | 'passport'>('national_id');
  const [docNumber, setDocNumber] = useState('');
  const [idDoc, setIdDoc] = useState<File | null>(null);
  // A national ID has two sides and the reverse carries the district and place of issue. A passport
  // has none, so this stays null there and the backend refuses it if sent (UF-01 section 5.10).
  const [idBack, setIdBack] = useState<File | null>(null);
  const [selfie, setSelfie] = useState<File | null>(null);

  const needsBack = docType === 'national_id';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!idDoc || !selfie) {
      setError('We need a photo of your ID and a selfie.');
      return;
    }
    if (needsBack && !idBack) {
      setError('We need the back of your ID as well as the front.');
      return;
    }

    setBusy(true);
    setError(null);

    try {
      // The FILES go up first, and the SERVER hashes them. We are never asked for a content hash and
      // could not be trusted with one: a fingerprint the sender chooses is a fingerprint for a file
      // they may never have sent.
      const [document, selfieDoc, backDoc] = await Promise.all([
        api.uploadDocument(participantId, docType, idDoc),
        api.uploadDocument(participantId, 'selfie', selfie),
        needsBack && idBack
          ? api.uploadDocument(participantId, 'national_id_back', idBack)
          : Promise.resolve(null),
      ]);

      const result = await api.verifyTier1(participantId, {
        full_name: fullName,
        date_of_birth: dob,
        nationality,
        id_document_type: docType,
        id_document_number: docNumber,
        document_id: document.document_id,
        selfie_id: selfieDoc.document_id,
        ...(backDoc ? { document_back_id: backDoc.document_id } : {}),
      });

      onDone(result.status === 'under_review' ? 'under_review' : 'verified');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      {error ? <FormError message={error} /> : null}

      <div className="rounded-lg border border-info/30 bg-info/5 p-3 text-xs text-ink-muted">
        <strong className="font-medium text-info">Development mode.</strong> Document checks and
        liveness run through a <em>simulated</em> provider: nothing is really verified. The real
        provider is a commercial contract that is not yet in place.
      </div>

      <Field id="full-name" label="Full name, as it appears on your ID">
        <input
          id="full-name"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder="Mary Anne Wanjiku"
          required
          minLength={2}
          className={INPUT}
        />
        <Help>
          Not a nickname. This exact name is what we screen, so a different one would check a person
          who does not exist.
        </Help>
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field id="dob" label="Date of birth">
          <input
            id="dob"
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            required
            className={INPUT}
          />
        </Field>

        <Field id="nationality" label="Nationality">
          {/* A searchable list, not a two-letter code box: nobody outside this codebase knows
              their ISO country code, and a typo in it is a failed verification. */}
          <CountrySelect id="nationality" value={nationality} onChange={setNationality} />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field id="doc-type" label="Document">
          <select
            id="doc-type"
            value={docType}
            onChange={(e) => setDocType(e.target.value as 'national_id' | 'passport')}
            className={INPUT}
          >
            <option value="national_id">National ID</option>
            <option value="passport">Passport</option>
          </select>
        </Field>

        <Field id="doc-number" label="Number">
          <input
            id="doc-number"
            value={docNumber}
            onChange={(e) => setDocNumber(e.target.value)}
            required
            minLength={4}
            className={`${INPUT} font-mono`}
          />
        </Field>
      </div>

      <Field
        id="id-doc"
        label={
          docType === 'passport'
            ? 'A photo of the biographical page of your passport'
            : 'A photo of the FRONT of your ID'
        }
      >
        <input
          id="id-doc"
          type="file"
          accept="image/*,application/pdf"
          capture="environment"
          onChange={(e) => setIdDoc(e.target.files?.[0] ?? null)}
          required
          className={FILE}
        />
        <Help>
          Lay it flat, fill the frame, and check there is no glare across the text before you send
          it. A photo we cannot read comes back to you as a retake.
        </Help>
      </Field>

      {/* Only for a national ID. A passport is a single page, and the backend refuses a reverse
          sent with one rather than quietly filing it. */}
      {needsBack ? (
        <Field id="id-back" label="A photo of the BACK of your ID">
          <input
            id="id-back"
            type="file"
            accept="image/*,application/pdf"
            capture="environment"
            onChange={(e) => setIdBack(e.target.files?.[0] ?? null)}
            required
            className={FILE}
          />
          <Help>
            The reverse carries your district and place of issue, so both sides are needed. It has
            to be a different photo from the front.
          </Help>
        </Field>
      ) : null}

      <Field id="selfie" label="A selfie">
        <input
          id="selfie"
          type="file"
          accept="image/*"
          capture="user"
          onChange={(e) => setSelfie(e.target.files?.[0] ?? null)}
          required
          className={FILE}
        />
        <Help>Matched against the photo on your document, so that a stolen ID is not enough.</Help>
      </Field>

      <Submit busy={busy} idle="Verify my identity" working="Verifying and screening…" />
    </form>
  );
}

/**
 * TIER 2. Proof of address, and where the money comes from.
 *
 * The source of funds is not paperwork. It is the baseline every later movement is judged against:
 * without it, the first time somebody moves two million there is nothing for that number to be
 * surprising against, and the risk engine's "unusual for this person" rule has no person to compare
 * them to.
 */
export function Tier2Form({
  participantId,
  onDone,
}: {
  participantId: string;
  onDone: (outcome: VerifyOutcome) => void;
}) {
  const { error, setError, fail } = useFail();
  const [busy, setBusy] = useState(false);

  const [line1, setLine1] = useState('');
  const [city, setCity] = useState('');
  const [county, setCounty] = useState('');
  const [proof, setProof] = useState<File | null>(null);
  const [category, setCategory] = useState<
    'salary' | 'business_income' | 'investment' | 'inheritance' | 'gift' | 'savings' | 'other'
  >('salary');
  const [description, setDescription] = useState('');
  const [volume, setVolume] = useState('');

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!proof) {
      setError('We need a proof of address.');
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const uploaded = await api.uploadDocument(participantId, 'proof_of_address', proof);

      const result = await api.verifyTier2(participantId, {
        address: { line1, city, county: county || undefined, country: 'KE' },
        address_proof_id: uploaded.document_id,
        source_of_funds: {
          category,
          description,
          // MINOR UNITS. The form asks for shillings; the API takes cents, because this figure is
          // compared against ledger amounts that are exact integers. Converting here, once, beats
          // discovering later that two halves of the system disagreed about the units of money.
          expected_monthly_volume: String(Math.round(Number(volume || '0') * 100)),
        },
      });

      onDone(result.status === 'under_review' ? 'under_review' : 'verified');
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      {error ? <FormError message={error} /> : null}

      <div className="rounded-lg bg-canvas p-3 text-xs leading-relaxed text-ink-muted">
        This is not paperwork. It is the baseline your account is judged against: if what you
        declare and what you do drift apart, we look. That is how a platform can be open to ordinary
        people and still be hard to launder money through.
      </div>

      <Field id="line1" label="Street address">
        <input
          id="line1"
          value={line1}
          onChange={(e) => setLine1(e.target.value)}
          required
          minLength={3}
          className={INPUT}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field id="city" label="Town or city">
          <input
            id="city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            required
            minLength={2}
            className={INPUT}
          />
        </Field>

        <Field id="county" label="County">
          <input
            id="county"
            value={county}
            onChange={(e) => setCounty(e.target.value)}
            className={INPUT}
          />
        </Field>
      </div>

      <Field id="proof" label="Proof of address">
        <input
          id="proof"
          type="file"
          accept="image/*,application/pdf"
          onChange={(e) => setProof(e.target.files?.[0] ?? null)}
          required
          className={FILE}
        />
        <Help>A utility bill or bank statement in your name, from the last three months.</Help>
      </Field>

      <Field id="sof" label="Source of funds">
        <select
          id="sof"
          value={category}
          onChange={(e) => setCategory(e.target.value as typeof category)}
          className={INPUT}
        >
          <option value="salary">Salary or employment</option>
          <option value="business_income">Business income</option>
          <option value="savings">Savings</option>
          <option value="investment">Investment returns</option>
          <option value="inheritance">Inheritance</option>
          <option value="gift">Gift</option>
          <option value="other">Other</option>
        </select>
      </Field>

      <Field id="sof-detail" label="Tell us a little more">
        <input
          id="sof-detail"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Nurse, Kenyatta National Hospital"
          required
          minLength={3}
          className={INPUT}
        />
      </Field>

      <Field id="volume" label="Roughly how much do you expect to move each month?">
        <div className="flex items-center gap-2">
          <span className="text-sm text-ink-muted">KES</span>
          <input
            id="volume"
            inputMode="decimal"
            value={volume}
            onChange={(e) => setVolume(e.target.value.replace(/[^\d.]/g, ''))}
            placeholder="80000"
            required
            className={`${INPUT} font-mono`}
          />
        </div>
        <Help>An estimate is fine. It is a baseline, not a limit.</Help>
      </Field>

      <Submit busy={busy} idle="Finish" working="Checking…" />
    </form>
  );
}

/**
 * The screening-hold screen.
 *
 * It says nothing about screening, and that is not squeamishness. ARCH 01 requires neutral messaging:
 * telling somebody they matched a sanctions or PEP list TIPS OFF a subject who may be under
 * investigation, which is itself an offence under most AML regimes. The compliance officer sees the
 * match; the participant sees this; the two messages differ ON PURPOSE.
 */
export function UnderReview() {
  return (
    <div className="py-6 text-center">
      <h2 className="font-semibold text-ink">We need a little longer</h2>
      <p className="mx-auto mt-2 max-w-sm text-sm text-ink-muted">
        Your checks are taking a bit more time than usual. We will let you know as soon as this is
        done. Nothing is wrong with your account.
      </p>
      {/* An onward action, so review is never a dead end. A held participant can still reach the parts
          of the app their current tier allows, and can get help, rather than being stranded here. */}
      <div className="mt-5 flex flex-col items-center gap-2">
        <Link
          to="/home"
          className="inline-flex items-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-light"
        >
          Go to home
        </Link>
        <Link to="/support" className="text-sm font-medium text-brand hover:underline">
          Contact support
        </Link>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------

// An input with no background and no text colour inherits the browser's, which is white-on-black
// the moment the app is in dark mode: the field looks empty while the participant is typing into
// it. `color-scheme: dark` additionally fixes the native date and file pickers, which ignore
// Tailwind entirely and render their own chrome.
const INPUT =
  'mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-ink placeholder:text-ink-muted outline-none focus:border-primary focus:ring-1 focus:ring-primary dark:[color-scheme:dark]';

const FILE = `${INPUT} file:mr-3 file:rounded file:border-0 file:bg-canvas file:px-3 file:py-1 file:text-xs`;

function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-ink">
        {label}
      </label>
      {children}
    </div>
  );
}

function Help({ children }: { children: React.ReactNode }) {
  return <p className="mt-1 text-xs text-ink-muted">{children}</p>;
}

function FormError({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="rounded-lg border border-danger/30 bg-danger/5 p-3 text-sm text-danger"
    >
      {message}
    </div>
  );
}

function Submit({ busy, idle, working }: { busy: boolean; idle: string; working: string }) {
  return (
    <button
      type="submit"
      disabled={busy}
      className="w-full rounded-lg bg-primary py-2.5 text-sm font-medium text-white transition hover:bg-primary-light disabled:opacity-50"
    >
      {busy ? working : idle}
    </button>
  );
}
